import React, { useEffect, useState, useContext } from 'react';
import {
  Typography, Table, Button, Modal, Form, Input, Space, Popconfirm, message,
} from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import api from '../api';
import { UserContext } from '../App';

export default function SkillExclusionsAdminPage() {
  const currentUser = useContext(UserContext);
  const isAdmin     = currentUser?.Role === 'admin';
  const [rows, setRows]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [modalOpen, setModal]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [form] = Form.useForm();

  const load = () => {
    setLoading(true);
    api.get('/api/admin/skill-exclusions')
      .then((r) => setRows(r.data))
      .catch(() => message.error('Failed to load skill exclusions.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    let values;
    try { values = await form.validateFields(); } catch { return; }
    setSaving(true);
    try {
      await api.post('/api/admin/skill-exclusions', {
        cx_skill_no: values.cx_skill_no,
        skill_name:  values.skill_name || null,
      });
      message.success('Skill exclusion added.');
      setModal(false);
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to add skill exclusion.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/api/admin/skill-exclusions/${id}`);
      message.success('Skill exclusion removed.');
      load();
    } catch (err) {
      message.error(err.response?.data?.error || 'Failed to remove skill exclusion.');
    }
  };

  const columns = [
    {
      title: 'CX Skill No',
      dataIndex: 'CXSkillNo',
      key: 'CXSkillNo',
      width: 140,
      sorter: (a, b) => a.CXSkillNo - b.CXSkillNo,
    },
    {
      title: 'Skill Name',
      dataIndex: 'SkillName',
      key: 'SkillName',
      render: (v) => v || <Typography.Text type="secondary">—</Typography.Text>,
    },
    {
      title: 'Added',
      dataIndex: 'CreatedAt',
      key: 'CreatedAt',
      width: 170,
      render: (v) => v ? v.slice(0, 16).replace('T', ' ') : '—',
    },
    ...(isAdmin ? [{
      title: '',
      key: 'actions',
      width: 80,
      render: (_, record) => (
        <Popconfirm
          title="Remove this exclusion?"
          description="Calls from this skill will be included in stats again."
          onConfirm={() => handleDelete(record.ID)}
          okText="Remove"
          okButtonProps={{ danger: true }}
        >
          <Button size="small" danger icon={<DeleteOutlined />} />
        </Popconfirm>
      ),
    }] : []),
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>Skill Exclusions</Typography.Title>
          <Typography.Text type="secondary">
            Skills inserted here will be excluded when calculating call statistics.
          </Typography.Text>
        </div>
        {isAdmin && (
          <Button type="primary" icon={<PlusOutlined />} onClick={() => { form.resetFields(); setModal(true); }}>
            Add Skill
          </Button>
        )}
      </div>

      <Table
        rowKey="ID"
        dataSource={rows}
        columns={columns}
        loading={loading}
        size="small"
        pagination={{ defaultPageSize: 25, showTotal: (t) => `${t} excluded skills` }}
        style={{ marginTop: 12 }}
      />

      <Modal maskClosable={false}
        title="Exclude a Skill"
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModal(false)}
        confirmLoading={saving}
        okText="Add"
        width={380}
        destroyOnClose
      >
        <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
          <Form.Item
            label="CX Skill No"
            name="cx_skill_no"
            rules={[
              { required: true, message: 'Skill number is required' },
              { pattern: /^\d+$/, message: 'Must be a number' },
            ]}
            extra="The skill_no value from Superior.CallLog / CXOne."
          >
            <Input placeholder="e.g. 48291" />
          </Form.Item>
          <Form.Item
            label="Skill Name"
            name="skill_name"
            extra="Optional — for your reference only."
          >
            <Input placeholder="e.g. Training Skills" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
